import {
  CheckCircleIcon,
  InfoIcon,
  SpinnerGapIcon,
  WarningIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import React, { type JSX } from "react";
import { toast, Toaster } from "sonner";

type Status = "success" | "info" | "warning" | "critical";

type Options = {
  /** Automatically hide after this number of milliseconds */
  hideAfter?: number;
  /** Optional actions to display alongside the notification */
  actions?: JSX.Element | React.FC<{ close: () => void }>;
  /** Placement of actions relative to the message */
  actionsPlacement?: "top" | "bottom";
};

const defaultDurations: Record<Status, number> = {
  success: 5000,
  info: 5000,
  warning: 10_000,
  critical: 15_000,
};

const toastFn: Record<Status, typeof toast.success> = {
  success: toast.success,
  info: toast.info,
  warning: toast.warning,
  critical: toast.error,
};

const publish =
  (status: Status) =>
  (message: React.ReactNode, options: Options = {}) => {
    const duration = options.hideAfter ?? defaultDurations[status];

    if (typeof options.actions === "function") {
      const renderFn = options.actions;
      const id = toastFn[status](message, {
        duration,
        description: renderFn({
          close: () => toast.dismiss(id),
        }) as React.ReactNode,
      });
      return;
    }

    toastFn[status](message, {
      duration,
      description: options.actions,
    });
  };

export const notify: Record<
  Status,
  (message: React.ReactNode, options?: Options) => void
> = {
  critical: publish("critical"),
  warning: publish("warning"),
  info: publish("info"),
  success: publish("success"),
};

export const notifyCatch = (message: Error | React.ReactNode) =>
  publish("critical")(message instanceof Error ? message.message : message);

/**
 * Render one instance of `GlobalNotification` at the app root.
 * Use the `notify` functions to trigger notifications from anywhere.
 */
export const GlobalNotification: React.FC = () => (
  <Toaster
    position="bottom-center"
    richColors
    theme="light"
    icons={{
      success: <CheckCircleIcon className="size-4" />,
      info: <InfoIcon className="size-4" />,
      warning: <WarningIcon className="size-4" />,
      error: <XCircleIcon className="size-4" />,
      loading: <SpinnerGapIcon className="size-4 animate-spin" />,
    }}
    toastOptions={{
      style: {
        fontFamily: "var(--font-family)",
        borderRadius: "var(--radius-md)",
      },
    }}
  />
);
